import { Badge, Button } from "../base";
import { cn } from "../../utils/cn";
import { motion } from "framer-motion";

export function HeroSection({
    badge,
    title,
    description,
    buttons,
    image,
    clientsLabel,
    clients,
    ...rest
}) {
    return (
        <section
            {...rest}
            className="relative overflow-hidden bg-gradient-to-b from-[#e8f9fd] via-sky-50/40 to-white dark:from-base-900 dark:to-base-950"
        >
            {/* Decorative blobs */}
            <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-cyan-100/30 blur-[100px]" />
                <div className="absolute top-40 -left-20 w-[300px] h-[300px] rounded-full bg-sky-200/20 blur-[80px]" />
                <div className="absolute top-60 -right-10 w-[250px] h-[250px] rounded-full bg-teal-100/20 blur-[80px]" />
            </div>

            <div className="container max-w-6xl px-4 sm:px-6 lg:px-8 mx-auto relative z-10">
                <div className="flex flex-col justify-center items-center pt-16 pb-0">
                    <div className="flex flex-col justify-center items-center gap-5 text-center max-w-3xl mx-auto pb-12 sm:pb-16">
                        {/* Badge */}
                        {badge && (
                            <motion.span
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium tracking-wide border border-cyan-300 bg-cyan-50/80 text-cyan-500 backdrop-blur-sm"
                            >
                                {badge.label}
                            </motion.span>
                        )}

                        {/* Headline */}
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.6,
                                delay: 0.2,
                                ease: [0.25, 0.46, 0.45, 0.94],
                            }}
                            className="text-5xl sm:text-6xl lg:text-7xl font-display font-semibold leading-[1.1] tracking-tight title-gradient"
                        >
                            {title}
                        </motion.h1>

                        {/* Description */}
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.6,
                                delay: 0.35,
                                ease: "easeOut",
                            }}
                            className="text-base sm:text-lg text-base-500 dark:text-base-400 max-w-xl leading-relaxed"
                        >
                            {description}
                        </motion.p>

                        {/* CTA Buttons */}
                        {buttons && buttons.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.6,
                                    delay: 0.5,
                                    ease: "easeOut",
                                }}
                                className="flex flex-wrap justify-center items-center gap-3 mt-4"
                            >
                                {buttons.map((button, index) => (
                                    <Button key={index} {...button} />
                                ))}
                            </motion.div>
                        )}

                        {/* Social proof */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.65 }}
                            className="text-xs text-base-400 dark:text-base-600 mt-2"
                        >
                            Dipercaya oleh{" "}
                            <span className="font-semibold text-base-600 dark:text-base-400">
                                120+ klinik
                            </span>{" "}
                            di seluruh Indonesia
                        </motion.p>
                    </div>

                    {/* Product image */}
                    {image && (
                        <motion.div
                            initial={{ opacity: 0, y: 50, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.5,
                                ease: [0.25, 0.46, 0.45, 0.94],
                            }}
                            className="w-full max-w-4xl mx-auto mb-0 relative"
                        >
                            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-3/4 h-16 bg-cyan-300/30 blur-2xl rounded-full" />
                            <img
                                src={image.src}
                                alt={image.alt}
                                className={cn(
                                    "w-full h-auto block relative z-10 rounded-t-2xl",
                                    image.className,
                                )}
                            />
                        </motion.div>
                    )}
                </div>
            </div>
        </section>
    );
}
